/** Insights section body for the Product detail page. */
import * as React from "react";
import { ChevronDown, ChevronUp, Coins, Sparkles } from "lucide-react";
import { Text } from "../../../../ui/components/Text";
import { AuthTransitionScreen } from "../../../../ui/components/AuthTransitionScreen";
import { useT } from "../../../lib/i18n";
import * as insightsClient from "../../../lib/api/insights";
import type { InsightsApi, InsightUsageSummary, ProductInsight } from "../../../lib/api/insights";
import { InsightCard, URGENCY_RANK, formatUsd, type InsightActions } from "./InsightCard";
import * as s from "./ProductInsightsSection.css";

/** Open headlines shown before "Show N more", so the section stays scannable. */
const VISIBLE_OPEN_LIMIT = 3;

export function ProductInsightsSection({
  productId,
  token,
  showUsage = false,
  canDismiss = false,
  onOpenCountChange,
  onViewEvidence,
  refreshKey = 0,
  api = insightsClient,
}: {
  productId: string;
  token: string | null;
  /** Show model/token/cost details (super-admins). */
  showUsage?: boolean;
  /** Dismiss requires edit access, matching alert dismiss. */
  canDismiss?: boolean;
  onOpenCountChange?: (count: number) => void;
  onViewEvidence?: InsightActions["onViewEvidence"];
  /** Changes when a live update says Insights changed; triggers a quiet refetch. */
  refreshKey?: number;
  api?: InsightsApi;
}) {
  const t = useT();
  const [insights, setInsights] = React.useState<ProductInsight[] | null>(null);
  const [loadError, setLoadError] = React.useState(false);
  const [usage, setUsage] = React.useState<InsightUsageSummary | null>(null);
  const [showResolved, setShowResolved] = React.useState(false);
  const [showAllOpen, setShowAllOpen] = React.useState(false);

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoadError(false);
    api
      .fetchProductInsights(token, productId)
      .then((res) => {
        if (!cancelled) setInsights(res.data);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    if (showUsage) {
      api
        .fetchInsightUsage(token, productId)
        .then((u) => {
          if (!cancelled) setUsage(u);
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [api, token, productId, showUsage, refreshKey]);

  const open = React.useMemo(
    () =>
      (insights ?? [])
        .filter((i) => i.status === "open")
        .sort((a, b) => URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency] || b.createdAt.localeCompare(a.createdAt)),
    [insights],
  );
  const resolved = React.useMemo(() => (insights ?? []).filter((i) => i.status === "resolved"), [insights]);

  React.useEffect(() => {
    if (insights) onOpenCountChange?.(open.length);
  }, [insights, open.length, onOpenCountChange]);

  const replace = (next: ProductInsight) =>
    setInsights((prev) => prev?.map((i) => (i.id === next.id ? next : i)) ?? prev);

  const refreshUsage = () => {
    if (!token || !showUsage) return;
    api.fetchInsightUsage(token, productId).then(setUsage).catch(() => {});
  };

  const actions: InsightActions = {
    onLoadDetails: async (insight) => {
      if (!token) throw new Error("Not signed in");
      const next = await api.fetchInsightDetails(token, productId, insight.id);
      replace(next);
      refreshUsage();
      return next;
    },
    onFeedback: (insight, feedback) => {
      if (!token) return;
      replace({ ...insight, feedback });
      api.submitInsightFeedback(token, productId, insight.id, feedback).catch(() => replace(insight));
    },
    onDismiss: canDismiss
      ? (insight) => {
          if (!token) return;
          setInsights((prev) => prev?.filter((i) => i.id !== insight.id) ?? prev);
          api
            .dismissInsight(token, productId, insight.id)
            .catch(() => setInsights((prev) => (prev ? [...prev, insight] : prev)));
        }
      : undefined,
    onRequestSupport: async (insight, fields) => {
      if (!token) throw new Error("Not signed in");
      return api.requestInsightSupport(token, productId, insight.id, fields);
    },
    onViewEvidence,
  };

  if (loadError) {
    return <Text level="body-small" color="muted">{t("pro.products.insights.loadFailed")}</Text>;
  }
  if (!insights) {
    return <AuthTransitionScreen variant="compact" message={t("pro.products.insights.loading")} />;
  }

  return (
    <div className={s.root}>
      {open.length === 0 ? (
        <div className={s.empty}>
          <Sparkles size={20} aria-hidden />
          <Text level="body-small">{t("pro.products.insights.empty")}</Text>
        </div>
      ) : (
        <div className={s.list}>
          {(showAllOpen ? open : open.slice(0, VISIBLE_OPEN_LIMIT)).map((insight, i) => (
            <InsightCard key={insight.id} insight={insight} defaultExpanded={i === 0} showUsage={showUsage} {...actions} />
          ))}
          {open.length > VISIBLE_OPEN_LIMIT ? (
            <button
              type="button"
              className={s.resolvedToggle}
              aria-expanded={showAllOpen}
              onClick={() => setShowAllOpen((v) => !v)}
            >
              {showAllOpen ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
              {showAllOpen
                ? t("pro.products.insights.showFewer")
                : t("pro.products.insights.showMore", { count: open.length - VISIBLE_OPEN_LIMIT })}
            </button>
          ) : null}
        </div>
      )}

      {resolved.length > 0 ? (
        <div className={s.list}>
          <button
            type="button"
            className={s.resolvedToggle}
            aria-expanded={showResolved}
            onClick={() => setShowResolved((v) => !v)}
          >
            {showResolved ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
            {showResolved
              ? t("pro.products.insights.hideResolved", { count: resolved.length })
              : t("pro.products.insights.showResolved", { count: resolved.length })}
          </button>
          {showResolved
            ? resolved.map((insight) => <InsightCard key={insight.id} insight={insight} showUsage={showUsage} {...actions} />)
            : null}
        </div>
      ) : null}

      {showUsage && usage ? (
        <div className={s.usageSummary} title={t("pro.products.insights.usage.superAdminOnly")}>
          <Coins size={14} aria-hidden />
          <span>
            {t("pro.products.insights.usage.summary", {
              headlines: usage.headlines.count,
              templated: usage.headlines.templated,
              headlineCost: formatUsd(usage.headlines.costUsd),
              details: usage.details.count,
              detailsCost: formatUsd(usage.details.costUsd),
              total: formatUsd(usage.costUsd),
              skipped: usage.detailsSkipped.count,
              avoided: formatUsd(usage.detailsSkipped.estimatedCostAvoidedUsd),
            })}
          </span>
        </div>
      ) : null}
    </div>
  );
}
